import { useCallback, useEffect, useRef, useState } from "react";

import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
} from "@microsoft/signalr";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectStreamingApiBaseUrl,
    selectStreamingApiScopes,
} from "features/config/store/selectors";
import { SignalRHub } from "../types/SignalRHub";
import { SignalRInvoke } from "../types/SignalRInvoke";
import { messagesActions } from "../store/slice";
import { MsalInstanceService } from "features/auth/MsalInstanceService";

export const useSignalRHub = <TSignalRHub extends SignalRHub>(
    signalRHubConstructor: new () => TSignalRHub,
) => {
    const dispatch = useAppDispatch();

    const hubConnection = useRef<HubConnection>(undefined);

    const [isStarted, setIsStarted] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);

    const streamingApiScopes = useAppSelector(selectStreamingApiScopes);

    const streamingApiBaseUrl = useAppSelector(selectStreamingApiBaseUrl);

    // push all received messages to our messages store
    const onMessageReceivedHandler = useCallback(
        (messageType: string, ...args: unknown[]) => {
            console.debug(`Received signalr message of type ${messageType}`);

            dispatch(
                messagesActions.messageReceived({
                    messageType: messageType,
                    args: args,
                }),
            );
        },
        [dispatch],
    );

    // set up connection to SignalR hub
    useEffect(() => {
        const signalRHub = new signalRHubConstructor();

        const initConnection = async () => {
            const hubUrl = `${streamingApiBaseUrl}/message/hubs/${signalRHub.hubName}`;

            hubConnection.current = new HubConnectionBuilder()
                .withUrl(hubUrl, {
                    accessTokenFactory: async () => {
                        const token =
                            await MsalInstanceService.getInstance().acquireTokenAsync(
                                streamingApiScopes,
                            );

                        if (!token)
                            throw new Error("No access token available");

                        return token;
                    },
                })
                .configureLogging(LogLevel.Information)
                .withAutomaticReconnect()
                .build();

            try {
                setIsStarted(false);
                setIsError(false);

                await hubConnection.current.start().then(() => {
                    setIsStarted(true);
                });

                signalRHub.clientMethodNames.forEach((methodName) => {
                    if (hubConnection.current) {
                        hubConnection.current.on(
                            methodName,
                            (...args: unknown[]) =>
                                onMessageReceivedHandler(methodName, ...args),
                        );
                    }
                });
            } catch (err) {
                console.error(err);

                setIsStarted(false);
            }
        };

        const closeConnection = async () => {
            if (hubConnection.current) {
                signalRHub.clientMethodNames.forEach((methodName) => {
                    if (hubConnection.current) {
                        hubConnection.current.off(
                            methodName,
                            onMessageReceivedHandler,
                        );
                    }
                });

                await hubConnection.current.stop();
                setIsStarted(false);
            }
        };

        if (!hubConnection.current && streamingApiBaseUrl) {
            initConnection();
        }

        return () => {
            closeConnection();

            // Make sure we empty the hubconnection on cleanup
            hubConnection.current = undefined;
        };
    }, [
        onMessageReceivedHandler,
        signalRHubConstructor,
        streamingApiBaseUrl,
        streamingApiScopes,
    ]);

    const invokeOnHub = useCallback(
        async (invokeCall: SignalRInvoke) => {
            if (hubConnection.current && isStarted) {
                return await hubConnection.current.invoke(
                    invokeCall.methodName,
                    invokeCall.methodArgs,
                );
            }

            throw new Error("No connection available");
        },
        [isStarted],
    );

    return {
        isStarted,
        isError,
        invokeOnHub,
    };
};
